// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { ApiError, describeError as describeApiError } from '../../../utils/serverErrors';
import { BasePrice } from '../BaseRates/baseRates';
import { ChangedModel, ChangeSetEntityType } from '../ChangeSetImpact/changeSetImpact';
import { PRICING_MODELS_ROUTE } from '../PricingModels/PricingModelsList';
import { ChangeSetBase, ChangeSetStatus, ChangeSetSummary } from '../PricingModels/pricingModels';
import { scopingModelRoute } from '../ScopingModels/scopingModels';
import { Ter, terGroupPath } from '../TerGroups/terGroups';

export const CHANGE_SETS_ROUTE = '/settings/changeSets';

export const changeSetRoute = (changeSetId: string): string =>
  `${CHANGE_SETS_ROUTE}/${encodeURIComponent(changeSetId)}`;

export const changeSetPath = (changeSetId: string): string =>
  `/changeSets/${encodeURIComponent(changeSetId)}`;

export const READ_ONLY_NOTE = 'You can view change sets but not review them.';

/** The records whose change set regenerates the live models that depend on them. */
export type ModelInputType = 'baseRate' | 'terGroup';

/**
 * What a base-rate or TER-group update returns with a 202 instead of the written record, when a
 * live model depends on the record: the change set proposed for review.
 */
export interface ProposedChangeSet extends ChangeSetSummary {
  // Live models it cannot regenerate because their live version has no stored inputs; may be empty.
  skippedModels?: ChangedModel[];
}

/**
 * The newest change set proposed for a base-rate or TER-group record, as the record's GET returns
 * it. Both are null when none was proposed or it expired; a newer proposal replaces them, and a
 * review does not clear them, so the status tells whether it still awaits review. Optional because
 * only the live record's GET promises them; a past version or a write's response may omit them.
 */
export interface LatestChangeSet {
  latestChangeSetId?: string | null;
  latestChangeSetStatus?: ChangeSetStatus | null;
  // Only while that change set is failed: its appliedModels and totalModels.
  latestChangeSetAppliedModels?: number;
  latestChangeSetTotalModels?: number;
}

/** How many of a change set's live models were made live before its approval failed. */
export interface ApplyProgress {
  appliedModels: number;
  totalModels: number;
}

/** Both counts, or null when either is absent. */
export const applyProgressOf = (
  appliedModels: number | undefined,
  totalModels: number | undefined
): ApplyProgress | null =>
  typeof appliedModels === 'number' && typeof totalModels === 'number'
    ? { appliedModels, totalModels }
    : null;

// Pricing's 409 for an approval that failed part way, e.g. "Change set cs-9 failed after applying
// 1 of 2 models; approve it again to finish". It counts only the models that approval had left.
const FAILED_APPROVAL_PATTERN = /failed after applying (\d+) of (\d+) models/;

/** The counts in Pricing's refusal of an approval that failed part way; null for any other. */
export const applyProgressIn = (message: string): ApplyProgress | null => {
  const match = FAILED_APPROVAL_PATTERN.exec(message);
  return match ? { appliedModels: Number(match[1]), totalModels: Number(match[2]) } : null;
};

/** What to tell a reviewer about a change set whose approval failed part way. */
export const failedApprovalMessage = (progress: ApplyProgress | null): string =>
  progress
    ? `Approval failed after applying ${progress.appliedModels} of ${progress.totalModels} models. Retry to apply the rest.`
    : 'Approval failed after applying some models. Retry to apply the rest.';

/** Whether a record's newest change set awaits approval, again too after a partial failure. */
export const isAwaitingApproval = (
  record: LatestChangeSet
): record is { latestChangeSetId: string; latestChangeSetStatus: 'pending' | 'failed' } =>
  Boolean(record.latestChangeSetId) &&
  (record.latestChangeSetStatus === 'pending' || record.latestChangeSetStatus === 'failed');

/** Whether an update's response is a proposed change set rather than the record it wrote. */
export const isProposedChangeSet = (response: unknown): response is ProposedChangeSet =>
  typeof response === 'object' && response !== null && 'changeSetId' in response;

/**
 * A change set as GET /changeSets/{changeSetId} returns it. A base-rate or TER-group change set
 * carries what it proposes and the live models it regenerates instead of a draft.
 */
export interface ChangeSetDetail extends ChangeSetBase {
  // The session's organization; for a TER-group change set, that of the admin who proposed it.
  organizationId: string;
  entityType: ChangeSetEntityType | ModelInputType;
  // The model's scheme id, the organization id of base rates, or the TER group's id.
  entityId: string;
  // Null for a base-rate change set.
  entityName: string | null;
  // Only on a base-rate change set; null when its stored rates are missing.
  proposedBasePrices?: BasePrice[] | null;
  // Only on a TER-group change set; null when they are missing. The two defaults are also absent
  // from a set proposed before there were two.
  proposedBilingualDefaultTer?: number | null;
  proposedMonolingualDefaultTer?: number | null;
  proposedTers?: Ter[] | null;
  // Only on a base-rate or TER-group change set; a TER group's models can be any organization's.
  regeneratedModels?: ChangedModel[];
  skippedModels?: ChangedModel[];
}

/**
 * Where to load the live version a base-rate or TER-group change set was proposed from, to compare
 * its proposed values with; null when it has no base version.
 */
export const baseVersionPath = (changeSet: ChangeSetDetail): string | null => {
  if (changeSet.baseVersion === null) {
    return null;
  }
  const recordPath =
    changeSet.entityType === 'terGroup' ? terGroupPath(changeSet.entityId) : '/baseRate';
  return `${recordPath}/version/${changeSet.baseVersion}`;
};

/** Approve is offered while pending, and again after an approval that failed part way. */
export const isApprovable = (changeSet: ChangeSetBase): boolean =>
  changeSet.status === 'pending' || changeSet.status === 'failed';

const MODEL_LABELS: Record<ChangeSetEntityType, string> = {
  pricingModel: 'Pricing model',
  scopingModel: 'Scoping model',
};

export const modelLabel = (entityType: ChangeSetEntityType): string => MODEL_LABELS[entityType];

export const modelPageRoute = (entityType: ChangeSetEntityType, id: string): string =>
  entityType === 'pricingModel'
    ? `${PRICING_MODELS_ROUTE}/${encodeURIComponent(id)}`
    : scopingModelRoute(id);

/**
 * The page of a model in the session's organization; null for another organization's model, which
 * the portal cannot show.
 */
export const modelRoute = (
  model: ChangedModel,
  sessionOrganizationId: string | number | null | undefined
): string | null => {
  if (model.organizationId === null || model.organizationId !== String(sessionOrganizationId)) {
    return null;
  }
  return modelPageRoute(model.entityType, model.id);
};

/** A model by name, falling back to its id when its record is missing. */
export const modelName = (model: ChangedModel): string => model.name || model.id;

const ACCESS_DENIED_MESSAGE = 'You do not have permission to view change sets.';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);
